// Spawning + the round "director": the phase-based timeline that decides what launches
// when (base rates, volleys, angry customers, parachutes, the Duplicate Storm). All the
// timing numbers here are playtested — CLAUDE.md §3.
import { G } from './state';
import { PRIO, POM_AIRBORNE_MUL, TICKET_SIZE_MUL } from './config';
import type { Prio } from './types';

// Scaled per-frame gravity.
export function grav(): number {
  return G.H * 0.00072;
}

// Launch from just below the bottom edge, arcing up into view.
function launchSpec(sz: number): { x: number; y: number; vx: number; vy: number } {
  const x = G.W * 0.12 + Math.random() * G.W * 0.76;
  const vx = ((G.W * 0.5 - x) * (0.4 + Math.random() * 0.5)) / 60 + (Math.random() - 0.5) * 1.6;
  const peak = G.H * (0.14 + Math.random() * 0.22);
  const vy = -Math.sqrt(2 * grav() * (G.H - peak + 60));
  return { x, y: G.H + 50 * sz, vx, vy };
}

export function spawnTicket(prio: Prio): void {
  const p = PRIO[prio];
  const r = (Math.min(G.W, G.H) * 0.055 * p.size + 18) * TICKET_SIZE_MUL;
  const l = launchSpec(1);
  G.objs.push({
    kind: 'ticket',
    prio,
    r,
    x: l.x,
    y: l.y,
    vx: l.vx * p.speed,
    vy: l.vy * (0.96 + 0.08 * p.speed),
    rot: Math.random() * 6,
    vr: (Math.random() - 0.5) * 0.06,
  });
}

export function spawnAngry(): void {
  const r = Math.min(G.W, G.H) * 0.052 + 18;
  const l = launchSpec(1);
  G.objs.push({ kind: 'angry', r, x: l.x, y: l.y, vx: l.vx, vy: l.vy, rot: 0, vr: (Math.random() - 0.5) * 0.04, fuse: 0 });
}

export function spawnChute(): void {
  const r = Math.min(G.W, G.H) * 0.055 + 20;
  G.objs.push({ kind: 'chute', r, x: G.W * 0.2 + Math.random() * G.W * 0.6, y: -90, vx: 0, vy: G.H * 0.0016, sway: Math.random() * 6.28 });
}

export function spawnPom(): void {
  const r = (Math.min(G.W, G.H) * 0.075 + 22) * POM_AIRBORNE_MUL;
  const l = launchSpec(1.2);
  G.objs.push({ kind: 'pom', r, x: l.x, y: l.y, vx: l.vx * 0.5, vy: l.vy * 0.92, rot: 0, vr: (Math.random() - 0.5) * 0.03 });
}

// Weighted priority pick by round phase.
function ticketMixAt(time: number): Prio {
  const roll = Math.random();
  if (time < 5) return roll < 0.4 ? 'P5' : roll < 0.75 ? 'P4' : 'P3';
  const p0ok = time >= 15;
  if (p0ok && roll < (time >= 40 ? 0.1 : 0.07)) return 'P0';
  if (roll < 0.2) return 'P1';
  if (roll < 0.4) return 'P2';
  if (roll < 0.62) return 'P3';
  if (roll < 0.82) return 'P4';
  return 'P5';
}

export function director(dt: number): void {
  const inFrenzy = G.t < G.frenzyUntil;
  if (inFrenzy) {
    G.spawnAcc += dt;
    if (G.spawnAcc > 0.15) {
      G.spawnAcc = 0;
      spawnTicket(Math.random() < 0.5 ? 'P4' : 'P5');
    }
    return;
  }
  // base spawn rate by phase (tickets/sec). The two busy phases are cut ~15% from the
  // original 3.2 / 4.0 to compensate for the 50%-larger tickets (which cover 2.25× the area)
  // — keeps peak volley moments from cluttering a phone screen. Intro phases unchanged.
  let rate = G.t < 5 ? 1.1 : G.t < 15 ? 1.6 : G.t < 40 ? 2.7 : 3.4;
  // breathing room around the Duplicate Storm: thin the queue while it's incoming/airborne
  const pomSoon = (!G.pomDone && G.t >= G.nextPom - 1.5) || G.objs.some((o) => o.kind === 'pom' && !o.dead);
  if (pomSoon) rate *= 0.5;
  G.spawnAcc += dt * rate;
  while (G.spawnAcc >= 1) {
    G.spawnAcc -= 1;
    spawnTicket(ticketMixAt(G.t));
  }
  // volleys (combo bait) after 15s — paused while the storm is around
  if (G.t >= 15 && !pomSoon) {
    G.volleyAcc += dt;
    const gap = G.t < 40 ? 6 : 4.5;
    if (G.volleyAcc > gap) {
      G.volleyAcc = 0;
      const n = 3 + Math.floor(Math.random() * (G.t < 40 ? 3 : 4));
      for (let i = 0; i < n; i++)
        setTimeout(() => {
          if (G.state === 'play') spawnTicket(ticketMixAt(G.t));
        }, i * 90);
      if (G.t >= 20 && Math.random() < 0.35)
        setTimeout(() => {
          if (G.state === 'play') spawnAngry();
        }, n * 45);
    }
  }
  // angry customers: first ~6s, then periodic
  if (G.t >= G.nextAngry) {
    spawnAngry();
    G.nextAngry = G.t + (G.t < 15 ? 6 : G.t < 40 ? 5 + Math.random() * 1.5 : 3.5 + Math.random() * 1.5);
  }
  // parachutes: ~12s, maybe ~45s
  if (G.t >= G.nextChute && G.nextChute > 0) {
    spawnChute();
    if (G.nextChute < 40) {
      G.nextChute = 44 + Math.random() * 6;
    } else G.nextChute = -1;
  }
  // Duplicate Storm (pomegranate): once, ~16-20s
  if (!G.pomDone && G.t >= G.nextPom) {
    spawnPom();
    G.pomDone = true;
  }
}
